import { type ClientFormData } from '../types';

/** JWT reserved claim names that cannot be used as custom claim keys */
export const RESERVED_CLAIMS = [
  'iss',
  'sub',
  'aud',
  'exp',
  'iat',
  'jti',
  // Domain-specific: Saviynt injects roles under this claim automatically,
  // so user-defined custom claims must not collide with it.
  'saviynt_roles',
] as const;

/**
 * Checks a parsed claims object for reserved claim names.
 * Returns the list of reserved names found (empty if none).
 */
export function getReservedClaims(claims: Record<string, unknown>): string[] {
  return Object.keys(claims).filter((key) =>
    (RESERVED_CLAIMS as readonly string[]).includes(key)
  );
}

/**
 * Validates a custom claims JSON string.
 * Returns an error message string, or null if valid.
 */
export function validateCustomClaims(jsonString: string): string | null {
  let parsed: unknown;

  try {
    parsed = JSON.parse(jsonString);
  } catch {
    return 'Custom claims must be valid JSON.';
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return 'Custom claims must be a JSON object.';
  }

  const reserved = getReservedClaims(parsed as Record<string, unknown>);

  if (reserved.length > 0) {
    return `Reserved claim names are not allowed: ${reserved.join(', ')}.`;
  }

  return null;
}

/**
 * Validates a JWKS URL string.
 * Must be a valid HTTPS URL. Returns an error message or null if valid.
 */
export function validateJwksUrl(url: string): string | null {
  const trimmed = url.trim();

  if (!trimmed) {
    return 'A JWKS URL is required.';
  }

  let parsed: URL;

  try {
    parsed = new URL(trimmed);
  } catch {
    return 'Enter a valid URL (e.g. https://example.com/.well-known/jwks.json).';
  }

  if (parsed.protocol !== 'https:') {
    return 'JWKS URL must use HTTPS.';
  }

  return null;
}

/**
 * Extracts per-field error messages from an API error response body.
 * Expects the shape: `{ errors: [{ field: string, message: string }] }`.
 * Returns a Record keyed by field name, suitable for merging into fieldErrors.
 */
export function extractApiFieldErrors(body: unknown): Record<string, string> {
  if (!body || typeof body !== 'object') {
    return {};
  }

  const { errors } = body as { errors?: unknown };

  if (!Array.isArray(errors)) {
    return {};
  }

  interface FieldError {
    field: string;
    message: string;
  }

  const isFieldError = (entry: unknown): entry is FieldError =>
    !!entry &&
    typeof entry === 'object' &&
    typeof (entry as FieldError).field === 'string' &&
    typeof (entry as FieldError).message === 'string';

  return (errors as unknown[])
    .filter(isFieldError)
    .reduce<Record<string, string>>((acc, { field, message }) => {
      acc[field] = message;

      return acc;
    }, {});
}

/**
 * Validates the client form fields.
 * Returns a Record<fieldName, errorMessage> — empty object means no errors.
 */
export function validateClientForm(
  data: Partial<ClientFormData>
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!data.clientName?.trim()) {
    errors.clientName = 'Client name is required.';
  }

  if (!data.roles || data.roles.length === 0) {
    errors.roles = 'At least one role is required.';
  }

  if (data.customClaims?.trim()) {
    const claimsError = validateCustomClaims(data.customClaims);

    if (claimsError) {
      errors.customClaims = claimsError;
    }
  }

  // Key source is only required during create. In edit mode it can be empty
  // (meaning no change to the existing key source).
  if (data.keySource) {
    if (data.keySource === 'certificate' && !data.publicCert) {
      errors.publicCert = 'A public certificate file is required.';
    } else if (data.keySource === 'jwksUrl') {
      const urlError = validateJwksUrl(data.jwksUrl ?? '');

      if (urlError) {
        errors.jwksUrl = urlError;
      }
    }
  }

  return errors;
}
